import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, UploadCloud, ExternalLink, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import RichTextEditor from "@/components/RichTextEditor";
import { Field, TextInput } from "@/components/Field";
import AdminHint from "@/components/admin/AdminHint";

const PAGES = [
  { key: "home", label: "Home", path: "/" },
  { key: "about_qhht", label: "About QHHT", path: "/about-qhht" },
  { key: "about_isabelle", label: "About Isabelle", path: "/about-isabelle" },
  { key: "what_to_expect", label: "What to Expect", path: "/what-to-expect" },
  { key: "volunteer", label: "Free Sessions", path: "/volunteer" },
  { key: "faq", label: "FAQ", path: "/faq" },
  { key: "testimonials", label: "Testimonials", path: "/testimonials" },
  { key: "contact", label: "Contact", path: "/contact" },
];

const emptyHero = { eyebrow: "", script: "", title: "", intro: "" };

export default function PagesPanel() {
  const { reload } = useSite();
  const [all, setAll] = useState({});
  const [active, setActive] = useState("home");
  const [hero, setHero] = useState(emptyHero);
  const [body, setBody] = useState("");
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  const loadAll = async () => {
    const { data } = await api.get("/admin/content");
    setAll(data);
    return data;
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const d = all[active]?.draft || {};
    setHero({ ...emptyHero, ...(d.hero || {}) });
    setBody(d.body_html || "");
    setCards(d.cards || []);
    setEditorKey((k) => k + 1); // remount editor with new content
  }, [active, all]);

  const dirty =
    JSON.stringify(all[active]?.draft || {}) !==
    JSON.stringify(all[active]?.published || {});

  const updCard = (i, key, val) => setCards(cards.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  const addCard = () => setCards([...cards, { title: "", body: "" }]);
  const removeCard = (i) => setCards(cards.filter((_, idx) => idx !== i));

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/content/${active}`, { hero, body_html: body, cards });
      await loadAll();
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save.");
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/content/${active}`, { hero, body_html: body, cards });
      await api.post(`/admin/content/${active}/publish`);
      await loadAll();
      await reload();
      toast.success("Published — your changes are now live.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not publish.");
    } finally { setBusy(false); }
  };

  const current = PAGES.find((p) => p.key === active);

  return (
    <div className="grid lg:grid-cols-[220px,1fr] gap-8">
      <div className="lg:col-span-2">
        <AdminHint>
          Choose a page on the left, then edit its heading and main text. Press <strong>Save draft</strong> to keep your
          work, and <strong>Publish</strong> when you’re ready for visitors to see it. Small labels and buttons live in the <strong>Text</strong> tab.
        </AdminHint>
      </div>
      <aside className="space-y-1">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setActive(p.key)}
            data-testid={`page-tab-${p.key}`}
            className={`w-full text-left rounded-xl px-4 py-2.5 font-body text-sm transition-colors ${
              active === p.key ? "bg-brand-forest text-white" : "text-brand-slate hover:bg-brand-accent"
            }`}
          >
            {p.label}
          </button>
        ))}
      </aside>

      <div className="rounded-3xl bg-white border border-brand-line p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <h2 className="font-heading text-2xl text-brand-ink">{current?.label}</h2>
            {dirty && <span className="rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs font-body">Unpublished changes</span>}
          </div>
          <div className="flex items-center gap-2">
            <a href={current?.path} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 font-body text-sm text-brand-slate hover:bg-brand-accent">
              <ExternalLink size={15} strokeWidth={1.5} /> View
            </a>
            <button onClick={saveDraft} disabled={busy} data-testid="save-draft-btn" className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 font-body text-sm text-brand-ink hover:bg-brand-accent disabled:opacity-60">
              <Save size={15} strokeWidth={1.5} /> Save draft
            </button>
            <button onClick={publish} disabled={busy} data-testid="publish-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-forest text-white px-4 py-2 font-body text-sm hover:bg-brand-forest-dark disabled:opacity-60">
              <CheckCircle2 size={15} strokeWidth={1.5} /> Publish
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <p className="font-body text-sm uppercase tracking-[0.2em] text-brand-lavender-dark">Page heading</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Eyebrow (small label)"><TextInput value={hero.eyebrow} onChange={(e) => setHero({ ...hero, eyebrow: e.target.value })} data-testid="hero-eyebrow" /></Field>
            <Field label="Script line (line 2)"><TextInput value={hero.script} onChange={(e) => setHero({ ...hero, script: e.target.value })} data-testid="hero-script" /></Field>
          </div>
          <Field label="Title (line 3)"><TextInput value={hero.title} onChange={(e) => setHero({ ...hero, title: e.target.value })} data-testid="hero-title" /></Field>
          <Field label="Intro paragraph"><TextInput value={hero.intro} onChange={(e) => setHero({ ...hero, intro: e.target.value })} data-testid="hero-intro" /></Field>

          <p className="font-body text-sm uppercase tracking-[0.2em] text-brand-lavender-dark pt-2">Page content</p>
          <RichTextEditor key={editorKey} value={body} onChange={setBody} />
          <p className="font-body text-xs text-brand-slate/70 flex items-center gap-2">
            <UploadCloud size={14} strokeWidth={1.5} /> Tip: use the toolbar for headings, bold text, bullet lists and quotes. Save a draft to keep your work, then Publish to make it live.
          </p>

          {(active === "home" || cards.length > 0) && (
            <div className="pt-4" data-testid="cards-editor">
              <div className="flex items-center justify-between mb-3">
                <p className="font-body text-sm uppercase tracking-[0.2em] text-brand-lavender-dark">Feature cards</p>
                <button type="button" onClick={addCard} data-testid="add-card-btn" className="inline-flex items-center gap-1.5 rounded-full border border-brand-line px-3 py-1.5 font-body text-sm text-brand-ink hover:bg-brand-accent">
                  <Plus size={14} strokeWidth={1.5} /> Add card
                </button>
              </div>
              <div className="space-y-4">
                {cards.map((c, i) => (
                  <div key={i} className="rounded-2xl border border-brand-line p-4" data-testid={`card-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-body text-xs text-brand-slate">Card {i + 1}</span>
                      <button type="button" onClick={() => removeCard(i)} data-testid={`remove-card-${i}`} className="text-rose-500 hover:bg-rose-50 rounded-lg p-1.5">
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </div>
                    <TextInput className="mb-2" value={c.title} onChange={(e) => updCard(i, "title", e.target.value)} data-testid={`card-title-${i}`} placeholder="Card heading" />
                    <textarea
                      value={c.body}
                      onChange={(e) => updCard(i, "body", e.target.value)}
                      data-testid={`card-body-${i}`}
                      rows={3}
                      placeholder="Card text"
                      className="w-full bg-transparent border border-brand-line rounded-xl focus:outline-none focus:border-brand-forest px-3 py-2 font-body text-sm text-brand-ink resize-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
